import React, { useState, Fragment } from 'react';
import { PageElement, ElementType, QuizConfig } from '../types';
import ElementRenderer from './ElementRenderer';
import { generateId } from '../constants';
import ElementEditor from './ElementEditor';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PageElementsEditorProps {
  elements: PageElement[];
  onUpdate: (elements: PageElement[]) => void;
  onElementDrop?: (elementType: ElementType, index: number) => void;
  onSelectElement?: (element: PageElement, index: number) => void;
  selectedElementId?: string | null;
  embedEditor?: boolean;
  previewMode?: boolean;
  config?: QuizConfig;
}

const PageElementsEditor: React.FC<PageElementsEditorProps> = ({ elements, onUpdate, onElementDrop, onSelectElement, selectedElementId, embedEditor = true, config, previewMode }) => {

  React.useEffect(() => {
    if (embedEditor) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const interacting = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if (interacting) return;
      if (!selectedElementId) return;
      const idx = elements.findIndex(el => el.id === selectedElementId);
      if (idx < 0) return;

      if (e.key === 'Delete') {
        e.preventDefault();
        const newEls = elements.filter((_, i) => i !== idx);
        onUpdate(newEls);
        return;
      }

      if ((e.key.toLowerCase() === 'd') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const copy: PageElement = { ...(JSON.parse(JSON.stringify(elements[idx]))), id: generateId() } as PageElement;
        const newEls = [...elements];
        newEls.splice(idx + 1, 0, copy);
        onUpdate(newEls);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [embedEditor, selectedElementId, elements, onUpdate]);


  const createElement = (type: ElementType): PageElement => {
    const baseElement = {
      id: generateId(),
      type,
    };

    switch (type) {
      case ElementType.Title:
        return { ...baseElement, type: ElementType.Title, content: 'Novo Título', align: 'center' } as any;
      case ElementType.Text:
        return { ...baseElement, type: ElementType.Text, content: 'Novo texto...', align: 'center' } as any;
      case ElementType.Alert:
        return { ...baseElement, type: ElementType.Alert, content: 'Mensagem do alerta', variant: 'error' };
      case ElementType.Timer:
        return { ...baseElement, type: ElementType.Timer, content: 'Resgate agora seu desconto: [time]', durationSeconds: 60, variant: 'warning' } as any;
      case ElementType.Options:
        return {
          ...baseElement,
          type: ElementType.Options,
          title: 'Qual a questão a ser respondida?',
          description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
          options: [
            { id: generateId(), text: 'Opção 1' },
            { id: generateId(), text: 'Opção 2' },
            { id: generateId(), text: 'Opção 3' },
            { id: generateId(), text: 'Opção 4' }
          ],
          multiple: false,
          layout: 'grid2',
          imageOrientation: 'horizontal',
          verticalImageRatio: '1:1',
          spacing: 'medium',
          style: 'simple',
          checkStyle: 'traditional',
          checkPosition: 'before',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.List:
        return {
          ...baseElement,
          type: ElementType.List,
          items: [
            { title: 'Benefício 1', description: 'Descrição do benefício com <strong>HTML</strong> e emojis 🙂' },
            { title: 'Benefício 2', description: 'Texto de apoio com detalhes e exemplos.' },
            { title: 'Benefício 3', description: 'Use <em>ênfase</em>, quebras de linha e símbolos.' }
          ],
          titlePosition: 'top',
          layout: 'list',
          border: 'small',
          shadow: 'none',
          spacing: 'medium',
          textAlign: 'left'
        } as any;
      case ElementType.Price:
        return {
          ...baseElement,
          type: ElementType.Price,
          items: [
            { title: 'Plano Start', value: 'R$ 59,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Pro', prefix: '10% off', value: 'R$ 89,90', suffix: 'à vista', topHighlight: '⭐ Mais Escolhido', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Gold', prefix: '10% off', value: 'R$ 129,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false }
          ],
          layout: 'horizontal',
          verticalAlign: 'bottom',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.Level:
        return {
          ...baseElement,
          type: ElementType.Level,
          title: 'Nível',
          subtitle: 'Fusce vitae tellus',
          percent: 75,
          indicatorText: '',
          legendLeft: 'Normal',
          legendCenter: 'Médio',
          legendRight: 'Alto',
          showMeter: true,
          showProgress: true,
          styleType: 'dashes',
          colorTheme: 'theme',
          border: 'none',
          shadow: 'none',
          backgroundColor: (config?.colors.background || '#ffffff'),
          titleColor: (config?.colors.title || '#1e293b'),
          textColor: (config?.colors.text || '#64748b'),
          barColor: (config?.colors.primary || '#000000')
        } as any;
      case ElementType.QA:
        return {
          ...baseElement,
          type: ElementType.QA,
          items: [
            { question: 'Qual a primeira dúvida a ser resolvida?', answer: 'Este é apenas um texto de exemplo utilizado para ilustrar como a resposta de uma dúvida frequente será exibida nesta seção.' },
            { question: 'Descreva outra dúvida a ser resolvida.', answer: 'Texto genérico de demonstração. Serve apenas como modelo visual para representar uma resposta real a ser inserida posteriormente.' }
          ],
          openFirst: true,
          allowMultiple: false,
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.Button:
        return { ...baseElement, type: ElementType.Button, text: 'Continuar', widthPercent: 100, height: 'medium' } as any;
      case ElementType.Image:
        return { ...baseElement, type: ElementType.Image, url: '', alt: '' };
      case ElementType.Audio:
        return { ...baseElement, type: ElementType.Audio, url: '', question: '' };
      case ElementType.Spacer:
        return { ...baseElement, type: ElementType.Spacer, height: 20 };
      case ElementType.Loading:
        return { ...baseElement, type: ElementType.Loading, title: 'Analisando...', subtitle: 'Estamos analisando suas respostas...', durationSeconds: 10, showPercent: true, barColor: (config?.colors.primary || '#000000') } as any;
      case ElementType.Testimonial:
        return {
          ...baseElement,
          type: ElementType.Testimonial,
          name: 'Nome da pessoa',
          info: '@usuario',
          stars: 5,
          text: '"Escreva aqui o depoimento..."',
          imageUrl: '',
          starColor: '#f59e0b',
          border: 'small',
          shadow: 'small'
        } as any;
      case ElementType.Form:
        return {
          ...baseElement,
          type: ElementType.Form,
          fieldId: '',
          title: 'Texto',
          fieldType: 'text',
          placeholder: '',
          mask: '',
          required: false,
          fontSize: 'normal',
          labelPosition: 'tradicional',
          align: 'left',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.SlotMachine:
        return {
          ...baseElement,
          type: ElementType.SlotMachine,
          title: 'Tente a Sorte',
          winningMessage: 'PARABÉNS! VOCÊ GANHOU!',
          losingMessage: 'Que pena, tente novamente!',
          subMessage: 'Clique abaixo para resgatar!',
          maxAttempts: 3,
          alwaysWin: true,
          align: 'center',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.Roulette:
        return {
          ...baseElement,
          type: ElementType.Roulette,
          title: 'Gire a Roleta',
          buttonText: 'Girar',
          winningMessage: 'Você ganhou: {prize}',
          subMessage: 'Parabéns!',
          items: [
            { id: generateId(), emoji: '🎁', label: 'Presente' },
            { id: generateId(), emoji: '😀', label: 'Sorte' },
            { id: generateId(), emoji: '💰', label: 'Dinheiro' },
            { id: generateId(), emoji: '🎁', label: 'Presente' },
            { id: generateId(), emoji: '😀', label: 'Sorte' },
            { id: generateId(), emoji: '💰', label: 'Dinheiro' },
          ],
          maxItems: 6,
          align: 'center',
          border: 'small',
          shadow: 'none'
        } as any;
      default:
        throw new Error(`Tipo de elemento desconhecido: ${type}`);
    }
  };

  const updateElement = (index: number, updatedElement: PageElement) => {
    const newElements = [...elements];
    newElements[index] = updatedElement;
    onUpdate(newElements);
  };

  const deleteElement = (index: number) => {
    const newElements = elements.filter((_, i) => i !== index);
    onUpdate(newElements);
  };

  const moveElement = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      onUpdate(newElements);
    } else if (direction === 'down' && index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      onUpdate(newElements);
    }
  };

  return (
    <div className="space-y-4">

      {elements.length === 0 ? (
        <div>
          <DroppableSlot index={0} active={false} fullArea />
        </div>
      ) : (
        <div className="space-y-0">
          <DroppableSlot index={0} active={false} />

          <SortableContext items={elements.map(e => e.id)} strategy={verticalListSortingStrategy}>
            {elements.map((element, index) => (
              <Fragment key={element.id}>
                <SortableItem id={element.id}>
                  {embedEditor ? (
                    <ElementEditor
                      element={element}
                      onUpdate={(updatedElement) => updateElement(index, updatedElement)}
                      onDelete={() => deleteElement(index)}
                      onMoveUp={index > 0 ? () => moveElement(index, 'up') : undefined}
                      onMoveDown={index < elements.length - 1 ? () => moveElement(index, 'down') : undefined}
                      config={config}
                    />
                  ) : (
                    <div
                      className={`relative ${selectedElementId === element.id ? 'outline outline-2 outline-sky-500' : ''}`}
                      style={{ outlineOffset: 2 }}
                      onClick={() => onSelectElement?.(element, index)}
                    >
                      <ElementRenderer element={element} config={config || {
                        colors: { background: '#ffffff', primary: '#000000', title: '#1e293b', text: '#64748b' },
                        fontSizes: { title: '1.7rem', subtitle: '0.9rem', option: '1rem' },
                        fontFamily: 'Inter',
                        contentAlignment: 'top',
                        contentWidth: 'small',
                        elementSize: 'small',
                        spacing: 'medium',
                        borderRadius: 'light',
                        header: { showBackButton: true, showStepIndicator: true, progressBarStyle: 'default' }
                      }} previewMode={!!previewMode} />
                      {selectedElementId === element.id && (
                        <button
                          aria-label="Remover elemento"
                          title="Remover"
                          className="absolute top-1 right-1 z-10 w-6 h-6 rounded-md bg-slate-200 text-slate-900 flex items-center justify-center border border-slate-300 hover:bg-slate-300 shadow-sm"
                          onClick={(e) => { e.stopPropagation(); deleteElement(index); }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3">
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M6 6l1 14h10l1-14" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </SortableItem>

                <DroppableSlot index={index + 1} active={false} />
              </Fragment>
            ))}
          </SortableContext>

          <DroppableSlot index={elements.length} active={false} />
        </div>
      )}
    </div>
  );
};

const DroppableSlot: React.FC<{ index: number; active: boolean; fullArea?: boolean }> = ({ index, active, fullArea = false }) => {
  const { isOver, setNodeRef } = useDroppable({ id: `slot-${index}` });
  return (
    <div
      ref={setNodeRef}
      className={fullArea
        ? `relative border-2 border-dashed rounded-lg p-6 text-center text-sm transition-all duration-200 ${(active || isOver) ? 'border-sky-500 bg-sky-50 text-sky-700 shadow-lg transform scale-105' : 'border-slate-200 text-slate-400'}`
        : `relative h-1 transition-all duration-200 ${(active || isOver) ? 'h-4 bg-gradient-to-r from-sky-500 to-blue-500 rounded-full shadow-md' : 'bg-transparent'}`
      }
    >
      {(active || isOver) && fullArea && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-xl border-2 border-white animate-pulse">↓</div>
      )}
      {(active || isOver) && !fullArea && (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-xl border-2 border-white animate-pulse">↓</div>
      )}
      {fullArea && (
        <div className={`transition-all duration-200 ${(active || isOver) ? 'text-sky-700 font-semibold' : ''}`}>Clique em um elemento ou arraste/solte aqui para adicioná-lo</div>
      )}
    </div>
  );
};

const SortableItem: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : undefined,
    cursor: 'grab'
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

export default PageElementsEditor;
